// src/pages/admin/AdminJobs.jsx
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabaseClient'
import { REGIONS } from '../../components/shared/regions'
import JobPostingForm from '../../components/Pharmacy/JobPostingForm'

export default function AdminJobs() {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('') // '', 'Open', 'Closed'
  const [region, setRegion] = useState('')
  const [district, setDistrict] = useState('')
  const [onlyUrgent, setOnlyUrgent] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortKey, setSortKey] = useState('created_desc') // 'created_desc' | 'date_asc' | 'date_desc'

  const [isCreating, setIsCreating] = useState(false)

  const availableDistricts = region ? REGIONS[region] || ['전체 (구)'] : REGIONS['']

  useEffect(() => {
    fetchJobs()
  }, [])

  async function fetchJobs() {
    setLoading(true)
    const { data, error } = await supabase
      .from('job_posts')
      .select(
        'id, created_by, pharmacy_name, title, description, date, time, region, district, location, department, urgent, status, created_at'
      )
      .order('created_at', { ascending: false })

    if (error) {
      console.error(error)
      setJobs([])
    } else {
      setJobs(data || [])
    }
    setLoading(false)
  }

  async function updateJob(jobId, patch) {
    const { error } = await supabase
      .from('job_posts')
      .update(patch)
      .eq('id', jobId)

    if (error) {
      alert('수정 실패: ' + error.message)
      return
    }
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j))
    )
  }

  const toggleStatus = (job) => {
    const current = job.status || 'Open'
    const next = current === 'Open' ? 'Closed' : 'Open'
    updateJob(job.id, { status: next })
  }

  const toggleUrgent = (job) => {
    updateJob(job.id, { urgent: !job.urgent })
  }

  const filteredAndSorted = useMemo(() => {
    const filtered = jobs.filter((j) => {
      const status = j.status || 'Open'

      const text = `${j.pharmacy_name ?? ''} ${j.title ?? ''} ${j.region ?? ''
        } ${j.district ?? ''} ${j.department ?? ''}`.toLowerCase()
      const matchQ = q ? text.includes(q.toLowerCase()) : true
      const matchStatus = statusFilter ? status === statusFilter : true
      const matchUrgent = onlyUrgent ? !!j.urgent : true
      const matchRegion = region ? j.region === region : true
      const matchDistrict = district ? j.district === district : true

      const jobDate = j.date || null
      const matchFrom = dateFrom ? (jobDate && jobDate >= dateFrom) : true
      const matchTo = dateTo ? (jobDate && jobDate <= dateTo) : true

      return (
        matchQ &&
        matchStatus &&
        matchUrgent &&
        matchRegion &&
        matchDistrict &&
        matchFrom &&
        matchTo
      )
    })

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'date_asc') {
        return (a.date || '').localeCompare(b.date || '')
      }
      if (sortKey === 'date_desc') {
        return (b.date || '').localeCompare(a.date || '')
      }
      // 기본: created_at 내림차순
      return (b.created_at || '').localeCompare(a.created_at || '')
    })

    return sorted
  }, [jobs, q, statusFilter, region, district, onlyUrgent, dateFrom, dateTo, sortKey])

  return (
    <div>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">📋 공고 관리</h2>
          <p className="text-xs text-gray-500 mt-1">
            약국에서 등록한 모든 공고를 한 화면에서 확인하고, 상태(Open/Closed)와 긴급 여부를 관리할 수 있습니다.
            <br />
            관리자 권한으로 직접 공고를 등록할 수도 있습니다.
          </p>
        </div>
        <button
          onClick={() => setIsCreating(!isCreating)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 shadow-sm"
        >
          {isCreating ? '닫기' : '공고 등록'}
        </button>
      </div>

      {isCreating && (
        <div className="mb-6 animate-fade-in-down">
          <JobPostingForm
            onPosted={() => {
              setIsCreating(false)
              fetchJobs()
            }}
            onCancelEdit={() => setIsCreating(false)}
          />
        </div>
      )}

      <div className="mb-4 p-3 bg-white border rounded-lg shadow-sm grid grid-cols-2 sm:flex sm:flex-wrap gap-3 items-end text-xs">
        <div className="col-span-2 sm:w-auto flex flex-col gap-1">
          <span className="text-gray-600">검색(약국/제목/지역/과목)</span>
          <input
            className="border rounded px-2 py-1 w-full sm:w-52"
            placeholder="예: 서울 강남 / 야간 / ○○약국"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-gray-600">상태</span>
          <select
            className="border rounded px-2 py-1 w-full sm:w-28"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">전체</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-gray-600">지역(시/도)</span>
          <select
            className="border rounded px-2 py-1 w-full sm:w-32"
            value={region}
            onChange={(e) => {
              setRegion(e.target.value)
              setDistrict('')
            }}
          >
            {Object.keys(REGIONS).map((r) => (
              <option key={r} value={r}>
                {r || '전체 (시/도)'}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-gray-600">지역(구/군)</span>
          <select
            className="border rounded px-2 py-1 w-full sm:w-32"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            disabled={!region}
          >
            {availableDistricts.map((d) => (
              <option key={d} value={d === '전체 (구)' ? '' : d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-gray-600">근무일자 From</span>
          <input
            type="date"
            className="border rounded px-2 py-1 w-full sm:w-36"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-gray-600">근무일자 To</span>
          <input
            type="date"
            className="border rounded px-2 py-1 w-full sm:w-36"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-1 ml-2 col-span-2 sm:col-span-1">
          <input
            type="checkbox"
            className="w-4 h-4 accent-rose-500"
            checked={onlyUrgent}
            onChange={(e) => setOnlyUrgent(e.target.checked)}
          />
          <span className="text-gray-700">긴급 공고만</span>
        </label>

        <div className="col-span-2 sm:ml-auto flex flex-col gap-1 w-full sm:w-auto mt-2 sm:mt-0">
          <span className="text-gray-600">정렬</span>
          <select
            className="border rounded px-2 py-1 w-full sm:w-40"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            <option value="created_desc">등록일 최신순</option>
            <option value="date_asc">근무일 빠른순</option>
            <option value="date_desc">근무일 늦은순</option>
          </select>
        </div>

        <button
          onClick={fetchJobs}
          className="col-span-2 sm:col-span-1 ml-0 sm:ml-2 px-3 py-1 rounded-md border text-xs text-gray-700 hover:bg-gray-50 h-[34px] w-full sm:w-auto"
        >
          새로고침
        </button>
      </div>

      {/* 리스트 */}
      <ul className="mt-3 grid gap-2">
        {loading && (
          <li className="h-20 rounded-xl bg-gray-50 animate-pulse" />
        )}
        {!loading && filteredAndSorted.length === 0 && (
          <li className="text-sm text-gray-500">
            조건에 맞는 공고가 없습니다.
          </li>
        )}
        {!loading &&
          filteredAndSorted.map((j) => {
            const status = j.status || 'Open'
            return (
              <li key={j.id} className="rounded-xl border p-3 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <div className="font-semibold text-sm">
                      {j.title}{' '}
                      {j.pharmacy_name && (
                        <span className="text-[11px] text-gray-500 ml-1">
                          ({j.pharmacy_name})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {j.region} {j.district && `· ${j.district}`} · {j.date}{' '}
                      · {j.time} ({j.location})
                    </div>
                    {j.department && (
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        과목: {j.department}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[11px] rounded-full border px-2 py-0.5 ${status === 'Open'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                          }`}
                      >
                        {status}
                      </span>
                      {j.urgent && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full border border-rose-200 text-rose-700 bg-rose-50">
                          🚨 긴급
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400">
                      등록일:{' '}
                      {j.created_at
                        ? new Date(j.created_at).toLocaleString()
                        : '-'}
                    </div>
                  </div>
                </div>

                {j.description && (
                  <div className="mt-2 text-xs text-gray-600 line-clamp-2">
                    {j.description}
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <button
                    onClick={() => toggleStatus(j)}
                    className="px-3 py-1 rounded-md border hover:bg-gray-50"
                  >
                    상태 변경(Open/Closed)
                  </button>
                  <button
                    onClick={() => toggleUrgent(j)}
                    className="px-3 py-1 rounded-md border hover:bg-gray-50"
                  >
                    긴급 토글
                  </button>
                </div>
              </li>
            )
          })}
      </ul>
    </div>
  )
}
